import type { FC } from 'react'
import { useMemo } from 'react'

import useTranslation from '@space-core/hooks/useTranslation'
import { useUserPermissions } from '@space-shell/common/hooks/useUserPermissions'
import {
  CommunityMemberPermissionsGroup,
  PermissionActions
} from '@space-shell/common/services/communities'
import { useGetCommunity } from '@space-shell/modules/communities/hooks'

import type { ModerationTableActionOption, ModerationTableActionProps } from '.'
import { ExpirationDateAction } from './expirationDateAction'
import { RemoveAction } from './removeAction'
import { RoleAction } from './roleAction'

const ModerationTableActions: FC<ModerationTableActionProps> = ({
  communityId,
  member,
  shouldRenderExpirationDate,
  isRemoveActionEnabled,
  refetchGetFollowersRoles
}) => {
  const { t } = useTranslation()
  const { communityData, isLoadingCommunityData } = useGetCommunity(Number(communityId))
  const { hasPermission } = useUserPermissions()
  const isTheOwner = communityData?.ownerId === member.id

  const options = useMemo<ModerationTableActionOption[]>(() => {
    const opts = [
      {
        label: t('configuration_sidebar.members_and_permissions.roles.admin'),
        value: CommunityMemberPermissionsGroup.ADMIN,
        requiredPermission: PermissionActions.ADMIN_UPDATE,
        isVisible: false
      },
      {
        label: t('configuration_sidebar.members_and_permissions.roles.moderator'),
        value: CommunityMemberPermissionsGroup.MODERATOR,
        requiredPermission: PermissionActions.MODERATOR_UPDATE,
        isVisible: true
      },
      {
        label: t('configuration_sidebar.members_and_permissions.roles.member'),
        value: CommunityMemberPermissionsGroup.FOLLOWER,
        requiredPermission: PermissionActions.MEMBER_UPDATE,
        isVisible: true
      }
    ]

    return opts
  }, [t])

  const canEdit = useMemo(() => {
    return options.some((opt) =>
      hasPermission(opt.requiredPermission, communityData?.permissions?.actions)
    )
  }, [communityData?.permissions?.actions, hasPermission, options])

  const canDelete = hasPermission(
    PermissionActions.MEMBER_DELETE,
    communityData?.permissions?.actions
  )

  const visibleOptions = options.filter((opt) => opt.isVisible)

  return (
    <>
      {shouldRenderExpirationDate && (
        <ExpirationDateAction
          isTheOwner={isTheOwner}
          canEdit={canEdit}
          communityId={Number(communityId)}
          isLoadingCommunityData={isLoadingCommunityData}
          member={member}
        />
      )}
      <RoleAction
        communityId={Number(communityId)}
        member={member}
        options={options}
        isLoadingCommunityData={isLoadingCommunityData}
        canEdit={canEdit}
        visibleOptions={visibleOptions}
        isTheOwner={isTheOwner}
        refetchGetFollowersRoles={refetchGetFollowersRoles}
      />
      {isRemoveActionEnabled && (
        <RemoveAction
          canDelete={canDelete}
          communityId={Number(communityId)}
          member={member}
          canEdit={canEdit}
          isLoadingCommunityData={isLoadingCommunityData}
          isTheOwner={isTheOwner}
          refetchGetFollowersRoles={refetchGetFollowersRoles}
        />
      )}
    </>
  )
}

export default ModerationTableActions
